import { useCallback, useMemo, useState, type ReactNode } from "react";
import dayjs from "dayjs";
import "dayjs/locale/sw";
import { en, type TranslationKey } from "./en";
import { sw } from "./sw";
import { I18nContext, type Language } from "./context";

const DICTIONARIES: Record<Language, Record<TranslationKey, string>> = { en, sw };
const STORAGE_KEY = "safiri.admin.lang";

function initialLanguage(): Language {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "en" || saved === "sw") return saved;
  } catch {
    // storage blocked — fall through
  }
  return "en";
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Language>(() => {
    const l = initialLanguage();
    dayjs.locale(l);
    document.documentElement.lang = l;
    return l;
  });

  const setLang = useCallback((l: Language) => {
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {
      // not persisted; still switch for this session
    }
    dayjs.locale(l);
    document.documentElement.lang = l;
    setLangState(l);
  }, []);

  const t = useCallback(
    (key: TranslationKey, vars?: Record<string, string | number>) => {
      let text: string = DICTIONARIES[lang][key] ?? en[key] ?? key;
      if (vars) {
        for (const [k, v] of Object.entries(vars)) {
          text = text.replaceAll(`{${k}}`, String(v));
        }
      }
      return text;
    },
    [lang],
  );

  const tx = useCallback(
    (key: string, fallback: string, vars?: Record<string, string | number>) =>
      key in en ? t(key as TranslationKey, vars) : fallback,
    [t],
  );

  const value = useMemo(() => ({ lang, setLang, t, tx }), [lang, setLang, t, tx]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
